/**
 * Session cache for live Amazon entities (campaigns, ad groups, keywords, search terms).
 *
 * Strategy (industry standard):
 * - Serve cached data on repeat visits (Bulk Ops ↔ Campaign Manager)
 * - Invalidate immediately after successful mutations (pause, bid, create, etc.)
 * - TTL as a safety net (30 min) if no mutation occurred
 * - forceRefresh bypasses cache (Refresh button / explicit reload)
 */

export const LIVE_ENTITIES = {
  CAMPAIGNS: 'campaigns',
  AD_GROUPS: 'adGroups',
  KEYWORDS: 'keywords',
  NEGATIVE_KEYWORDS: 'negativeKeywords',
  CAMPAIGN_NEGATIVE_KEYWORDS: 'campaignNegativeKeywords',
  SEARCH_TERMS: 'searchTerms',
  TARGETS: 'targets',
  PRODUCT_ADS: 'productAds',
  TARGETING_METRICS: 'targetingMetrics',
  CAMPAIGN_MANAGER_TARGETING: 'campaignManagerTargeting',
  KEYWORD_METRICS: 'keywordMetrics',
  AD_GROUP_METRICS: 'adGroupMetrics',
  PRODUCT_METRICS: 'productMetrics',
  DASHBOARD: 'dashboard',
  CACHED_REPORT: 'cachedReport',
  STORE_PRODUCTS: 'storeProducts',
  SB_THEMES: 'sbThemes',
};

const DEFAULT_TTL_MS = 30 * 60 * 1000;
const METRICS_TTL_MS = 15 * 60 * 1000;
// Ranges that include today/yesterday are topped up by Marketing Stream (near real-time),
// so keep them fresh; fully historical ranges are stable and can be cached longer.
const LIVE_METRICS_TTL_MS = 2 * 60 * 1000;

function reportToday() {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** TTL for a metrics request: short when the range touches the last ~2 days, otherwise long. */
/** Must match amazonApi.getSearchTerms fetchLiveCached scope. */
export function searchTermsCacheScope(campaignId, adGroupId, startDate, endDate) {
  return ['rollup-v1', campaignId || 'all', adGroupId || 'all', startDate, endDate].join(':');
}

export function metricsTtlFor(endDate) {
  if (!endDate) return LIVE_METRICS_TTL_MS; // period-based requests (e.g. "last 30 days") end today
  const today = reportToday();
  const cutoff = new Date(`${today}T00:00:00Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() - 2);
  return String(endDate).slice(0, 10) >= cutoff.toISOString().slice(0, 10)
    ? LIVE_METRICS_TTL_MS
    : METRICS_TTL_MS;
}

const store = new Map();

function buildKey(profileId, entity, scope = 'all') {
  return `${profileId}|${entity}|${scope}`;
}

export function getLiveCached(profileId, entity, scope = 'all') {
  const key = buildKey(profileId, entity, scope);
  const entry = store.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return null;
  }
  return entry.data;
}

export function setLiveCached(profileId, entity, scope, data, ttlMs = DEFAULT_TTL_MS) {
  const key = buildKey(profileId, entity, scope);
  store.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
    updatedAt: Date.now(),
  });
}

export function invalidateLiveEntityCache(profileId, entities = ['all']) {
  if (!profileId) return;
  const types = entities.includes('all')
    ? Object.values(LIVE_ENTITIES)
    : entities;
  const prefix = `${profileId}|`;

  for (const key of [...store.keys()]) {
    if (!key.startsWith(prefix)) continue;
    const entity = key.split('|')[1];
    if (types.includes(entity) || types.includes('all')) {
      store.delete(key);
    }
  }
}

export function clearAllLiveEntityCache() {
  store.clear();
}

export async function fetchLiveCached({
  profileId,
  entity,
  scope = 'all',
  forceRefresh = false,
  fetcher,
  ttlMs = DEFAULT_TTL_MS,
}) {
  if (!forceRefresh) {
    const hit = getLiveCached(profileId, entity, scope);
    if (hit !== null) return hit;
  }

  const data = await fetcher();
  let effectiveTtl = ttlMs;
  if (
    entity === LIVE_ENTITIES.SEARCH_TERMS
    || entity === LIVE_ENTITIES.KEYWORD_METRICS
    || entity === LIVE_ENTITIES.TARGETING_METRICS
    || entity === LIVE_ENTITIES.CAMPAIGN_MANAGER_TARGETING
  ) {
    const body = data?.data ?? data;
    const rows = body?.success && Array.isArray(body.data)
      ? body.data
      : (Array.isArray(body?.data) ? body.data : []);
    if (!rows.length) {
      effectiveTtl = Math.min(ttlMs, 45 * 1000);
    }
  }
  setLiveCached(profileId, entity, scope, data, effectiveTtl);
  return data;
}

export function resolveMutationInvalidation(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return null;

  const url = String(config.url || '');
  const isAmazonWrite = url.includes('/amazon/') || url.includes('/search-terms');
  if (!isAmazonWrite) return null;

  let body = config.data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = {};
    }
  }

  const profileId = config.params?.profileId || body?.profileId;
  if (!profileId) return null;

  if (url.includes('/campaigns')) {
    return {
      profileId,
      entities: [
        LIVE_ENTITIES.CAMPAIGNS,
        LIVE_ENTITIES.AD_GROUPS,
        LIVE_ENTITIES.DASHBOARD,
        LIVE_ENTITIES.CACHED_REPORT,
      ],
    };
  }
  if (url.includes('/adGroups')) {
    return { profileId, entities: [LIVE_ENTITIES.AD_GROUPS, LIVE_ENTITIES.KEYWORDS] };
  }
  if (
    url.includes('/keywords')
    || url.includes('/targets')
    || url.includes('negativeTargets')
    || url.includes('campaignNegativeTargets')
  ) {
    return {
      profileId,
      entities: [
        LIVE_ENTITIES.KEYWORDS,
        LIVE_ENTITIES.TARGETS,
        LIVE_ENTITIES.NEGATIVE_KEYWORDS,
        LIVE_ENTITIES.DASHBOARD,
        LIVE_ENTITIES.CACHED_REPORT,
        LIVE_ENTITIES.KEYWORD_METRICS,
      ],
    };
  }
  if (url.includes('negativeKeyword') || url.includes('negative-keywords') || url.includes('/sb/negativeKeywords')) {
    return {
      profileId,
      entities: [
        LIVE_ENTITIES.NEGATIVE_KEYWORDS,
        LIVE_ENTITIES.CAMPAIGN_NEGATIVE_KEYWORDS,
        LIVE_ENTITIES.SEARCH_TERMS,
        LIVE_ENTITIES.KEYWORDS,
      ],
    };
  }
  if (url.includes('/productAds') || url.includes('/product-ads')) {
    return { profileId, entities: [LIVE_ENTITIES.PRODUCT_ADS, LIVE_ENTITIES.PRODUCT_METRICS] };
  }
  if (url.includes('/sb/keywords')) {
    return { profileId, entities: [LIVE_ENTITIES.KEYWORDS, LIVE_ENTITIES.TARGETS] };
  }
  if (url.includes('/sb/themes')) {
    return {
      profileId,
      entities: [LIVE_ENTITIES.SB_THEMES, LIVE_ENTITIES.TARGETS, LIVE_ENTITIES.KEYWORDS],
    };
  }

  return null;
}

export { DEFAULT_TTL_MS, METRICS_TTL_MS };
